import React from 'react';
import { Check, ArrowRight, Home, UserPlus, Users, PiggyBank, Sprout } from 'lucide-react';
import { Button, GardenBackground } from '../components/ui';

export const FamilyCreationSuccess = ({
  family,
  guardian,
  user,
  onNavigate
}) => {
  return (
    <div className="min-h-screen relative">
      <GardenBackground />
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-lg">

          {/* Success icon */}
          <div className="flex justify-center mb-6">
            <div className="relative inline-block">
              <img src="/logo.svg" alt="GrowthSprouts" className="w-16 h-16" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />
              </div>
            </div>
          </div>

          {/* Main card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Header */}
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <img src="/logo.svg" alt="GrowthSprouts" className="w-9 h-9" />
                <span className="text-xl" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  <span className="text-emerald-700">Growth</span>
                  <span className="text-gray-800">Sprouts</span>
                </span>
              </div>
              <h1 className="text-2xl text-gray-900" style={{ fontFamily: "'DM Serif Display', serif" }}>
                You're all set!
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Your family account is ready to go
              </p>
            </div>

            {/* Account summary */}
            <div className="px-6 pb-5">
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Family</span>
                  <span className="text-sm font-medium text-gray-900">{family?.name}</span>
                </div>
                <div className="border-t border-gray-100" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Account</span>
                  <span className="text-sm font-medium text-gray-900">
                    {guardian?.first_name} {guardian?.last_name}
                  </span>
                </div>
                <div className="border-t border-gray-100" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm font-medium text-gray-900">{user?.email}</span>
                </div>
                {family?.program && (
                  <>
                    <div className="border-t border-gray-100" />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Program</span>
                      <span className="text-sm font-medium text-gray-900">{family.program}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Next steps */}
            <div className="px-6 pb-5">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                Get started
              </p>
              <div className="space-y-1">
                <button
                  onClick={() => onNavigate('addChild')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                    <UserPlus className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Add your kids</p>
                    <p className="text-xs text-gray-500">Create their savings accounts</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors" />
                </button>

                <button
                  onClick={() => onNavigate('familyDashboard')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Invite family members</p>
                    <p className="text-xs text-gray-500">Add other parents or guardians</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors" />
                </button>

                <button
                  onClick={() => onNavigate('familyDashboard')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors">
                    <PiggyBank className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Start saving</p>
                    <p className="text-xs text-gray-500">Begin the savings journey</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors" />
                </button>
              </div>
            </div>

            {/* Primary action */}
            <div className="px-6 pb-6">
              <Button
                size="lg"
                className="w-full"
                onClick={() => onNavigate('familyDashboard')}
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Back link */}
          <div className="text-center mt-4">
            <button
              onClick={() => onNavigate('welcome')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1"
            >
              <Home className="w-3.5 h-3.5" />
              Back to Welcome
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};